<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <div
        v-if="isLdapPolicy"
        :class="$style.messageContainer"
      >
        <LdapHostnameNotification
          :isItemBeingCreated="isItemBeingCreated"
        />
      </div>

      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          General Info
        </h2>
        <GuideMessage
          caretPosition="left"
          :class="$style.generalGuide"
          name="capGeneralInfo"
        />
        <GeneralInfoSectionv2
          :value="item || {}"
          @input="handleValueChange"
        />
      </div>

      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          Assignments
        </h2>
        <AssignmentsSection
          :previousValue="previousItem"
          :value="item"
          @valueChange="handleValueChange"
        />
      </div>

      <div
        v-if="shouldShowConditionsSection"
        :class="$style.sectionRoot"
      >
        <h2 :class="$style.sectionHeader">
          <span>Conditions (Optional)</span>
          <PremiumFeatureBadge
            v-if="showIcon"
            :class="$style.badge"
            :data-test-id="$testId('premiumBadge')"
          />
        </h2>
        <ConditionsSection
          :value="item"
          @input="handleValueChange"
        />
      </div>

      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          Action
        </h2>
        <ActionSection
          :value="item || {}"
          @input="handleValueChange"
        />
      </div>
    </template>
  </DetailsAsideLayout>
</template>

<script>
import { cloneDeep, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { authnPolicyType } from 'client/Security/AuthNPolicyType';
import { getLdapUrl } from 'client/Security/utils/AuthnPolicyUtilities';
import { ldapGuides, ssoGuides, userPortalGuides } from 'client/store/modules/guidedSetup/conditionalAccessConfig';
import { trialFeatures } from 'client/util/Constants/FeatureTrialConstants';
import ActionSection from 'client/Security/AuthNPoliciesAsidePanel/ActionSection';
import AssignmentsSection from 'client/Security/AuthNPoliciesAsidePanel/AssignmentsSection.vue';
import ConditionsSection from 'client/Security/AuthNPoliciesAsidePanel/ConditionsSection/ConditionsSection.vue';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import GeneralInfoSectionv2 from 'client/Security/AuthNPoliciesAsidePanel/GeneralInfoSectionv2.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import LdapHostnameNotification from 'client/Security/AuthNPoliciesAsidePanel/LdapHostnameNotification.vue';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import PremiumFeatureBadge from 'client/components/Badges/PremiumFeatureBadge.vue';

import { getMessageForError } from 'client/components/FormValidation/helpers/validationHelpers';

const authNPolicyModel = StoreModelMap[ItemTypeConsts.authNPolicies];

if (authNPolicyModel == null) {
  throw new Error('Vuex module "authNPolicyModel" is not defined');
}

export default {
  name: 'AuthNPoliciesAsidePanelSections',

  components: {
    ActionSection,
    AssignmentsSection,
    ConditionsSection,
    DetailsAsideLayout,
    GeneralInfoSectionv2,
    GuideMessage,
    LdapHostnameNotification,
    PremiumFeatureBadge,
  },

  data() {
    return {
      isLdapPolicyChecked: false,
    };
  },

  computed: {
    ...mapGetters(authNPolicyModel, [
      'item',
      'previousItem',
      'isItemBeingCreated',
    ]),

    ...mapGetters('FeatureTrialModule', [
      'getPremiumBadgeProps',
    ]),

    getLdapUrl,

    isLdapPolicy() {
      return this.item.type === authnPolicyType.ldap;
    },

    shouldShowConditionsSection() {
      return !this.isLdapPolicy;
    },

    showIcon() {
      return this.getPremiumBadgeProps(
        trialFeatures.zeroTrust.featureCode,
      ).nonHeaderBadgeVisibility;
    },
  },

  mounted() {
    // when creating a new policy, this value is missing, append ...
    const queryType = PageNavigationUtil.getSearchParams().type;
    const policyType = authnPolicyType[queryType];

    const hasId = this.item?.id;
    const isQueryType = !!queryType;
    const isPolicyType = this.item?.type !== policyType;

    const shouldChangeInitialValue = !hasId && isQueryType && isPolicyType;

    if (shouldChangeInitialValue) {
      let item = {
        ...this.item,
        type: policyType,
      };

      if (policyType === authnPolicyType.ldap) {
        // default ldap bind dn user checkbox to checked
        item = cloneDeep(item);
        item.targets.userAttributes = {
          ...item.targets.userAttributes,
          exclusions: [{ field: 'ldap_binding_user', operator: 'EQ', value: true }],
        };
      }

      this.handleValueChange(item);
    }
  },

  beforeMount() {
    const { guide: guideName, type } = this.$route.query;

    const guideConfig = get({
      application: ssoGuides,
      userportal: userPortalGuides,
      ldap: ldapGuides,
    }, type);

    const guide = get(guideConfig, guideName);

    if (guide) {
      this.initializeGuide({ type, guide });
    } else {
      this.dismissGuide();
    }
  },

  methods: {
    ...mapActions(authNPolicyModel, ['updateItem']),

    ...mapActions('GuidedSetupModule', [
      'initializeGuide',
      'dismissGuide',
    ]),

    getMessageForError,

    handleValueChange(newValue) {
      this.updateItem(newValue);
    },

    toggleLdapPolicy() {
      this.isLdapPolicyChecked = !this.isLdapPolicyChecked;
    },
  },
};
</script>

<style module>
  @import '../../css/sections.css';
  @import "../../components/FormValidation/css/formFields.css";

  .badge {
    margin-left: var(--jc-spacer-small);
  }

  .messageContainer {
    margin-bottom: var(--jc-spacer-small);
  }

  .generalGuide div {
    right: 40px;
    top: -10px;
  }

  .sectionHeader.sectionHeader.sectionHeader {
    align-items: center;
    display: flex;
    margin-bottom: 1em;
  }

  .ldapLabelWrapper {
    margin-top: 1em;
  }

  .ldapLabelText {
    margin-left: 0.5em;
  }

  .error {
    color: var(--jcError900);
    flex-basis: 100%;
    margin-left: 1.5em;
  }
</style>
